// @flow
import {trigger as edapTrigger} from './edapWrapper';
import type {SearchParamsType} from '../reducers/searchParams';
import type {ListingShape} from '../components/Hit/Hit';
import type {PriceSummaryShape} from '../components/Price/Price';
import type {PercentBookedResult} from '../queries/searchRequestQuery';
import type {JudgeResultType} from '../components/LeadCarouselContainer/CarouselsContainer';

import {logExperiment} from '@homeaway/abacus-client-utils';
import {VRBO_LANDING_PROPERTY_HERO_IMAGES_SELECTION} from '../abacus/experiments';
import {getJSONItem, MAB_PAYLOAD_ITEM} from '../utils/sessionStorage';
import {EdapCarouselNames} from '../components/LeadCarouselContainer/utils/constants';

export const SEARCH_RESULT_SET_PRESENTED = 'search_result_set.presented';
export const SERP_HIT_PRESENTED = 'serp_hit.presented';
export const VIRTUAL_TOUR_BADGE_PRESENTED = 'virtualtour_badge.presented';
export const SERP_HIT_MAB_PRESENTED = 'multi_armed_bandit.presented';
export const CONTENT_MODULE_PRESENTED = 'content_module.presented';
export const CONTENT_TILE_PRESENTED = 'content_tile.presented';
export const CONTENT_TILE_SELECTED = 'content_tile.selected';
export const MULTI_ARMED_BANDIT_PRESENTED = 'multi_armed_bandit.presented';
export const MULTI_ARMED_BANDIT_SELECTED = 'multi_armed_bandit.selected';
export const PROPERTY_HIT_PROMPT_PRESENTED = 'property_hit_prompt.presented';
export const PROPERTY_HIT_PROMPT_SELECTED = 'property_hit_prompt.selected';

export const triggerSearchResultSetPresentedEdapEvent = (
    inputParams: SearchParamsType,
    queryUUID: string,
    percentBooked: PercentBookedResult,
    listingIds?: string
) => {
    const {
        adultsCount,
        childrenCount,
        petIncluded,
        arrival,
        departure
    } = inputParams;

    const alphaRatio =
        !!percentBooked &&
        typeof percentBooked.currentPercentBooked !== 'undefined' &&
        percentBooked.currentPercentBooked !== null
            ? Math.round(percentBooked.currentPercentBooked * 100)
            : -1;

    const data = {
        action_location: 'search',
        adult_count: adultsCount ? `${adultsCount}` : null,
        child_count: childrenCount ? `${childrenCount}` : null,
        with_pets: String(petIncluded),
        arrival,
        departure,
        search_id: queryUUID,
        listing_ids: listingIds || '',

        // 'percent_booked' was a concern for competitor scraping, so give the field a vague name
        alpha_ratio: String(alphaRatio)
    };

    edapTrigger(SEARCH_RESULT_SET_PRESENTED, data);
};

export const getEdapEventJson = (priceSummary: ?PriceSummaryShape) => {
    let defaultNullObject = {price_accurate: null, total_price: null};
    if (!priceSummary) {
        return defaultNullObject;
    }
    try {
        return JSON.parse(priceSummary.edapEventJson);
    } catch (e) {
        return defaultNullObject;
    }
};

let serpHitPresentedEdapOccured = new Set();
export const triggerSerpHitPresentedEdapEvent = (listing: ListingShape) => {
    const {priceSummary, listingId, virtualTourBadge} = listing;
    if (serpHitPresentedEdapOccured.has(listingId)) {
        return;
    }

    const {
        // eslint-disable-next-line camelcase
        price_accurate,
        // eslint-disable-next-line camelcase
        total_price: total_price_available
    } = getEdapEventJson(priceSummary);

    const data = {
        action_location: 'search',
        listing_id: listingId,
        price_accurate,
        total_price_available
    };

    if (virtualTourBadge) {
        const virtualTourData = {
            action_location: 'search',
            tourid: '00000000-0000-0000-0000-000000000000'
        };
        edapTrigger(VIRTUAL_TOUR_BADGE_PRESENTED, virtualTourData);
    }

    edapTrigger(SERP_HIT_PRESENTED, data);
    serpHitPresentedEdapOccured.add(listingId);
};

let serpHitMabPresentedEdapOccured = new Set();
export const triggerSerpHitMabPresentedEdapEvent = (
    listing: ListingShape,
    havId: string,
    siteName: string,
    deviceType: string
) => {
    const {listingId, images} = listing;
    const mab = images && images.length && images[0].mab;
    const arm = mab && mab.arm;

    if (mab) {
        logExperiment(VRBO_LANDING_PROPERTY_HERO_IMAGES_SELECTION);
    }

    if (serpHitMabPresentedEdapOccured.has(listingId) || !arm) {
        return;
    }

    const data = {
        action_location: 'search',
        bandit_id: mab && mab.banditId,
        campaign_id: mab && mab.campaignId,
        cached: mab && mab.cached,
        visitor_id: havId,
        arm_id: {
            categoryName: arm && arm.categoryName,
            level: arm && arm.level
        },
        context: {monikerName: siteName, device: deviceType},
        payload_id: mab && mab.payloadId
    };

    edapTrigger(SERP_HIT_MAB_PRESENTED, data);
    serpHitMabPresentedEdapOccured.add(listingId);
};

export const triggerPresentedSerpRecentlyViewedCarouselEvent = (
    position: string
) => {
    const data = {
        action_location: 'recently_viewed_carousel_module',
        position_fired: position
    };

    edapTrigger(CONTENT_MODULE_PRESENTED, data);
};

export const triggerPresentedSerpAmenityCarouselEvent = (position: string) => {
    const data = {
        action_location: 'amenity_carousel_module',
        position_fired: position
    };

    edapTrigger(CONTENT_MODULE_PRESENTED, data);
};

export const triggerPresentedSerpFlexDatesCarouselEvent = (
    position: string
) => {
    const data = {
        action_location: 'flex_dates_module',
        position_fired: position
    };

    edapTrigger(CONTENT_MODULE_PRESENTED, data);
};

export const triggerPresentedSerpSimilarListingsCarouselEvent = () => {
    const data = {
        action_location: 'similar_listings_module'
    };

    edapTrigger(CONTENT_MODULE_PRESENTED, data);
};

export const triggerPresentedSerpCarouselCardEvent = (listingId: string) => {
    const data = {
        action_location: 'recently_viewed_carousel_module',
        event_action: 'recently_viewed_carousel_card_presented',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_PRESENTED, data);
};

export const triggerPresentedSerpAmenityCarouselCardEvent = (
    listingId: string
) => {
    const data = {
        action_location: 'amenity_carousel_module',
        event_action: 'amenity_carousel_card_presented',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_PRESENTED, data);
};

export const triggerPresentedSerpFlexDatesCarouselCardEvent = (
    listingId: string
) => {
    const data = {
        action_location: 'flex_dates_module',
        event_action: 'flex_dates_card_presented',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_PRESENTED, data);
};

export const triggerPresentedSerpSimilarListingsCarouselCardEvent = (
    listingId: string
) => {
    const data = {
        action_location: 'similar_listings_module',
        event_action: 'similar_listings_card_presented',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_PRESENTED, data);
};

export const triggerSelectedSerpCarouselCardEvent = (listingId: string) => {
    const data = {
        action_location: 'recently_viewed_carousel_module',
        event_action: 'recently_viewed_carousel_card_selected',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_SELECTED, data);
};

export const triggerSelectedSerpAmenityCarouselCardEvent = (
    listingId: string
) => {
    const data = {
        action_location: 'amenity_carousel_module',
        event_action: 'amenity_carousel_card_selected',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_SELECTED, data);
};

export const triggerSelectedSerpFlexDatesCarouselCardEvent = (
    listingId: string
) => {
    const data = {
        action_location: 'flex_dates_module',
        event_action: 'flex_dates_card_selected',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_SELECTED, data);
};

export const triggerSelectedSerpSimilarListingsCarouselCardEvent = (
    listingId: string
) => {
    const data = {
        action_location: 'similar_listings_module',
        event_action: 'similar_listings_card_selected',
        listingid: listingId
    };

    edapTrigger(CONTENT_TILE_SELECTED, data);
};

export const triggerLeadCarouselMabPresentedEvent = (
    judgeResult: JudgeResultType[],
    renderedCarousels: Object
) => {
    const edapJsonData = getJSONItem(MAB_PAYLOAD_ITEM);
    if (!edapJsonData) {
        return;
    }
    // eslint-disable-next-line camelcase
    const {armId, banditId, campaignId, context, payloadId} = edapJsonData;
    const data = {
        arm_id: armId,
        bandit_id: banditId,
        campaign_id: campaignId,
        context,
        payload_id: payloadId,
        event_properties: JSON.stringify(
            judgeResult.map(({component}) => {
                return {
                    name: EdapCarouselNames[component],
                    value: renderedCarousels[component]
                };
            })
        )
    };

    edapTrigger(MULTI_ARMED_BANDIT_PRESENTED, data);
};

export const triggerLeadCarouselMabSelectedEvent = () => {
    const edapJsonData = getJSONItem(MAB_PAYLOAD_ITEM);
    if (!edapJsonData) {
        return;
    }

    // eslint-disable-next-line camelcase
    const {armId, banditId, campaignId, context, payloadId} = edapJsonData;
    const data = {
        arm_id: armId,
        bandit_id: banditId,
        campaign_id: campaignId,
        context,
        payload_id: payloadId,
        reward_event_id: 0
    };

    edapTrigger(MULTI_ARMED_BANDIT_SELECTED, data);
};

export const triggerPropertyHitPromptPresentedEvent = (listingId: string) => {
    const data = {
        action_location: 'search',
        listing_id: listingId
    };

    edapTrigger(PROPERTY_HIT_PROMPT_PRESENTED, data);
};

export const triggerPropertyHitPromptSelectedEvent = (
    listingId: string,
    showMore: boolean
) => {
    const data = {
        action_location: 'search',
        listing_id: listingId,
        show_more: showMore
    };

    edapTrigger(PROPERTY_HIT_PROMPT_SELECTED, data);
};
